// HerbalSoapDetail.js - Herbal soap themed product detail component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Product Detail section with comprehensive product information
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the product detail is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The product detail elements
 */
export const createHerbalSoapDetailElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const detailSectionClass = 'herbal-soap-detail-section';
  const detailContainerClass = 'herbal-soap-detail-container';
  const detailContentClass = 'herbal-soap-detail-content';
  const detailImageSectionClass = 'herbal-soap-detail-image-section';
  const detailMainImageClass = 'herbal-soap-detail-main-image';
  const detailInfoSectionClass = 'herbal-soap-detail-info-section';
  const detailTitleClass = 'herbal-soap-detail-title';
  const detailPriceClass = 'herbal-soap-detail-price';
  const detailRatingClass = 'herbal-soap-detail-rating';
  const detailDescriptionClass = 'herbal-soap-detail-description';
  const detailSpecsClass = 'herbal-soap-detail-specs';
  const detailSpecItemClass = 'herbal-soap-detail-spec-item';
  const detailIngredientsClass = 'herbal-soap-detail-ingredients';
  const detailIngredientTagClass = 'herbal-soap-detail-ingredient-tag';
  const detailBenefitsClass = 'herbal-soap-detail-benefits';
  const detailBenefitItemClass = 'herbal-soap-detail-benefit-item';
  const detailActionsClass = 'herbal-soap-detail-actions';
  const detailQuantityClass = 'herbal-soap-detail-quantity';
  const detailWhatsAppButtonClass = 'herbal-soap-detail-whatsapp-button';
  const detailWebsiteButtonClass = 'herbal-soap-detail-website-button';

  // Desktop Styles - Herbal theme with natural green colors
  const detailSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    color: '#1a3d1a',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const detailContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  };

  const detailContentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'start'
  };

  const detailImageSectionStyles = {
    display: 'flex',
    justifyContent: 'center'
  };

  const detailMainImageStyles = {
    width: '100%',
    height: '500px',
    objectFit: 'cover',
    borderRadius: '16px',
    border: '2px solid rgba(144, 238, 144, 0.2)',
    boxShadow: '0 8px 30px rgba(34, 139, 34, 0.1)'
  };

  const detailInfoSectionStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const detailTitleStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.2'
  };

  const detailPriceStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#228b22',
    marginBottom: '16px',
    fontFamily: '"Inter", sans-serif'
  };

  const detailRatingStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px'
  };

  const detailDescriptionStyles = {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#4a5568',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const detailSpecsStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: '#f0fdf4',
    borderRadius: '12px',
    border: '1px solid rgba(144, 238, 144, 0.2)'
  };

  const detailSpecItemStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    fontFamily: '"Inter", sans-serif'
  };

  const detailIngredientsStyles = {
    marginBottom: '24px'
  };

  const detailIngredientTagStyles = {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: 'rgba(144, 238, 144, 0.15)',
    color: '#228b22',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    margin: '4px 8px 4px 0',
    border: '1px solid rgba(144, 238, 144, 0.3)'
  };

  const detailBenefitsStyles = {
    marginBottom: '32px'
  };

  const detailBenefitItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#4a5568',
    fontFamily: '"Inter", sans-serif'
  };

  const detailActionsStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  const detailQuantityStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  };

  const detailWhatsAppButtonStyles = {
    width: '100%',
    padding: '16px 24px',
    backgroundColor: '#228b22',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif'
  };

  const detailWebsiteButtonStyles = {
    width: '100%',
    padding: '16px 24px',
    backgroundColor: '#1a3d1a',
    color: '#ffffff',
    border: '2px solid #1a3d1a',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletDetailContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    alignItems: 'center'
  };

  const tabletDetailImageSectionStyles = {
    marginBottom: '20px'
  };

  const tabletDetailMainImageStyles = {
    maxWidth: '500px',
    height: '400px'
  };

  const tabletDetailInfoSectionStyles = {
    maxWidth: '800px',
    width: '100%'
  };

  const tabletDetailTitleStyles = {
    fontSize: '32px'
  };

  const tabletDetailPriceStyles = {
    fontSize: '28px'
  };

  // Mobile Styles
  const mobileDetailSectionStyles = {
    ...detailSectionStyles,
    paddingTop: '60px',
    paddingBottom: '60px'
  };

  const mobileDetailContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    alignItems: 'center'
  };

  const mobileDetailImageSectionStyles = {
    marginBottom: '20px'
  };

  const mobileDetailMainImageStyles = {
    maxWidth: '100%',
    height: '300px'
  };

  const mobileDetailInfoSectionStyles = {
    width: '100%'
  };

  const mobileDetailTitleStyles = {
    fontSize: '28px'
  };

  const mobileDetailPriceStyles = {
    fontSize: '24px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(detailSectionClass, detailSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(detailContainerClass, detailContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(detailContentClass, detailContentStyles);
    newRegistry[DEVICES.DESKTOP].set(detailImageSectionClass, detailImageSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(detailMainImageClass, detailMainImageStyles);
    newRegistry[DEVICES.DESKTOP].set(detailInfoSectionClass, detailInfoSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(detailTitleClass, detailTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(detailPriceClass, detailPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(detailRatingClass, detailRatingStyles);
    newRegistry[DEVICES.DESKTOP].set(detailDescriptionClass, detailDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(detailSpecsClass, detailSpecsStyles);
    newRegistry[DEVICES.DESKTOP].set(detailSpecItemClass, detailSpecItemStyles);
    newRegistry[DEVICES.DESKTOP].set(detailIngredientsClass, detailIngredientsStyles);
    newRegistry[DEVICES.DESKTOP].set(detailIngredientTagClass, detailIngredientTagStyles);
    newRegistry[DEVICES.DESKTOP].set(detailBenefitsClass, detailBenefitsStyles);
    newRegistry[DEVICES.DESKTOP].set(detailBenefitItemClass, detailBenefitItemStyles);
    newRegistry[DEVICES.DESKTOP].set(detailActionsClass, detailActionsStyles);
    newRegistry[DEVICES.DESKTOP].set(detailQuantityClass, detailQuantityStyles);
    newRegistry[DEVICES.DESKTOP].set(detailWhatsAppButtonClass, detailWhatsAppButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(detailWebsiteButtonClass, detailWebsiteButtonStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(detailTitleClass, tabletDetailTitleStyles);
    newRegistry[DEVICES.TABLET].set(detailPriceClass, tabletDetailPriceStyles);
    newRegistry[DEVICES.TABLET].set(detailContentClass, tabletDetailContentStyles);
    newRegistry[DEVICES.TABLET].set(detailImageSectionClass, tabletDetailImageSectionStyles);
    newRegistry[DEVICES.TABLET].set(detailMainImageClass, tabletDetailMainImageStyles);
    newRegistry[DEVICES.TABLET].set(detailInfoSectionClass, tabletDetailInfoSectionStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(detailSectionClass, mobileDetailSectionStyles);
    newRegistry[DEVICES.MOBILE].set(detailContentClass, mobileDetailContentStyles);
    newRegistry[DEVICES.MOBILE].set(detailImageSectionClass, mobileDetailImageSectionStyles);
    newRegistry[DEVICES.MOBILE].set(detailMainImageClass, mobileDetailMainImageStyles);
    newRegistry[DEVICES.MOBILE].set(detailInfoSectionClass, mobileDetailInfoSectionStyles);
    newRegistry[DEVICES.MOBILE].set(detailTitleClass, mobileDetailTitleStyles);
    newRegistry[DEVICES.MOBILE].set(detailPriceClass, mobileDetailPriceStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    detailSectionClass,
    detailContainerClass,
    detailContentClass,
    detailImageSectionClass,
    detailMainImageClass,
    detailInfoSectionClass,
    detailTitleClass,
    detailPriceClass,
    detailRatingClass,
    detailDescriptionClass,
    detailSpecsClass,
    detailSpecItemClass,
    detailIngredientsClass,
    detailIngredientTagClass,
    detailBenefitsClass,
    detailBenefitItemClass,
    detailActionsClass,
    detailQuantityClass,
    detailWhatsAppButtonClass,
    detailWebsiteButtonClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const contentId = sectionId + 2;
  
  // Image section IDs
  const imageSectionId = sectionId + 3;
  const mainImageId = sectionId + 4;
  
  // Info section IDs
  const infoSectionId = sectionId + 5;
  const titleId = sectionId + 6;
  const priceId = sectionId + 7;
  const ratingId = sectionId + 8;
  const descriptionId = sectionId + 9;
  const specsId = sectionId + 10;
  const ingredientsId = sectionId + 11;
  const benefitsId = sectionId + 12;
  const actionsId = sectionId + 13;
  const quantityId = sectionId + 14;
  const whatsAppId = sectionId + 15;
  const websiteId = sectionId + 16;

  // Product data
  const productData = {
    name: 'Premium Turmeric Herbal Soap',
    price: '$24.99',
    rating: 4.8,
    reviews: 127,
    description: 'Our premium turmeric herbal soap is handcrafted with 100% organic ingredients including turmeric, neem, coconut oil, and essential oils. This luxurious soap provides deep cleansing while nourishing your skin with natural antioxidants and anti-inflammatory properties.',
    mainImage: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    specifications: [
      { label: 'Weight', value: '100g' },
      { label: 'Type', value: 'Handmade Herbal Soap' },
      { label: 'Skin Type', value: 'All Skin Types' },
      { label: 'Certification', value: 'Organic Certified' },
      { label: 'Shelf Life', value: '24 Months' }
    ],
    ingredients: ['Turmeric', 'Neem', 'Coconut Oil', 'Olive Oil', 'Aloe Vera', 'Lavender Oil', 'Tea Tree Oil', 'Shea Butter'],
    benefits: [
      'Deep cleansing and purification',
      'Natural anti-inflammatory properties',
      'Moisturizes and nourishes skin',
      'Reduces acne and blemishes',
      'Brightens skin complexion',
      'Chemical-free and eco-friendly'
    ]
  };

  const newElements = [
    // Main Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Product Detail Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Product Detail Section',
      className: detailSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-section',
        'data-component': 'section',
        'data-section-type': 'product-detail',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Detail Container',
      parentId: sectionId,
      hierarchyName: 'Product Detail Container',
      className: detailContainerClass,
      styles: {},
      children: [contentId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-container'
      }
    },
    
    // Content Grid
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Detail Content',
      parentId: containerId,
      hierarchyName: 'Product Detail Content',
      className: detailContentClass,
      styles: {},
      children: [imageSectionId, infoSectionId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-content'
      }
    },
    
    // Image Section
    {
      id: imageSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Image Section',
      parentId: contentId,
      hierarchyName: 'Product Image Section',
      className: detailImageSectionClass,
      styles: {},
      children: [mainImageId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-image-section'
      }
    },
    
    // Main Product Image
    {
      id: mainImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Main Product Image',
      parentId: imageSectionId,
      hierarchyName: 'Main Product Image',
      className: detailMainImageClass,
      styles: {},
      children: [],
      text: '',
      src: productData.mainImage,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-main-image',
        'alt': productData.name
      }
    },
    
    // Info Section
    {
      id: infoSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Info Section',
      parentId: contentId,
      hierarchyName: 'Product Info Section',
      className: detailInfoSectionClass,
      styles: {},
      children: [titleId, priceId, ratingId, descriptionId, specsId, ingredientsId, benefitsId, actionsId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-info-section'
      }
    },
    
    // Product Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Product Title',
      parentId: infoSectionId,
      hierarchyName: 'Product Title',
      className: detailTitleClass,
      styles: {},
      children: [],
      text: productData.name,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-title'
      }
    },
    
    // Product Price
    {
      id: priceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Price',
      parentId: infoSectionId,
      hierarchyName: 'Product Price',
      className: detailPriceClass,
      styles: {},
      children: [],
      text: productData.price,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-price'
      }
    },
    
    // Product Rating
    {
      id: ratingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Rating',
      parentId: infoSectionId,
      hierarchyName: 'Product Rating',
      className: detailRatingClass,
      styles: {},
      children: [],
      text: `★★★★★ ${productData.rating} (${productData.reviews} reviews)`,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-rating'
      }
    },
    
    // Product Description
    {
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Product Description',
      parentId: infoSectionId,
      hierarchyName: 'Product Description',
      className: detailDescriptionClass,
      styles: {},
      children: [],
      text: productData.description,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-description'
      }
    }
  ];

  // Add specifications
  let currentSpecId = specsId + 100;
  const specsElement = {
    id: specsId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'div',
    name: 'Product Specifications',
    parentId: infoSectionId,
    hierarchyName: 'Product Specifications',
    className: detailSpecsClass,
    styles: {},
    children: [],
    text: '',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'herbal-soap-detail-specs'
    }
  };

  // Add spec title
  const specTitleId = currentSpecId++;
  newElements.push({
    id: specTitleId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'h4',
    name: 'Specifications Title',
    parentId: specsId,
    hierarchyName: 'Specifications Title',
    styles: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a3d1a',
      marginBottom: '16px',
      marginTop: '0'
    },
    children: [],
    text: 'Product Specifications',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'spec-title'
    }
  });

  specsElement.children.push(specTitleId);

  // Add specification items
  productData.specifications.forEach((spec, index) => {
    const specItemId = currentSpecId++;
    const specLabelId = currentSpecId++;
    const specValueId = currentSpecId++;

    newElements.push({
      id: specItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Spec Item ${index + 1}`,
      parentId: specsId,
      hierarchyName: `Spec Item ${index + 1}`,
      className: detailSpecItemClass,
      styles: {},
      children: [specLabelId, specValueId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-spec-item'
      }
    });

    newElements.push({
      id: specLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `Spec Label ${index + 1}`,
      parentId: specItemId,
      hierarchyName: `Spec Label ${index + 1}`,
      styles: {
        fontWeight: '600',
        color: '#1a3d1a'
      },
      children: [],
      text: spec.label,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'spec-label'
      }
    });

    newElements.push({
      id: specValueId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `Spec Value ${index + 1}`,
      parentId: specItemId,
      hierarchyName: `Spec Value ${index + 1}`,
      styles: {
        color: '#4a5568'
      },
      children: [],
      text: spec.value,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'spec-value'
      }
    });

    specsElement.children.push(specItemId);
  });

  newElements.push(specsElement);

  // Add ingredients section
  let currentIngredientId = ingredientsId + 200;
  const ingredientsElement = {
    id: ingredientsId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'div',
    name: 'Product Ingredients',
    parentId: infoSectionId,
    hierarchyName: 'Product Ingredients',
    className: detailIngredientsClass,
    styles: {},
    children: [],
    text: '',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'herbal-soap-detail-ingredients'
    }
  };

  // Add ingredients title
  const ingredientsTitleId = currentIngredientId++;
  newElements.push({
    id: ingredientsTitleId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'h4',
    name: 'Ingredients Title',
    parentId: ingredientsId,
    hierarchyName: 'Ingredients Title',
    styles: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a3d1a',
      marginBottom: '16px',
      marginTop: '0'
    },
    children: [],
    text: 'Natural Ingredients',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'ingredients-title'
    }
  });

  ingredientsElement.children.push(ingredientsTitleId);

  // Add ingredient tags
  productData.ingredients.forEach((ingredient, index) => {
    const ingredientTagId = currentIngredientId++;

    newElements.push({
      id: ingredientTagId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `Ingredient ${index + 1}`,
      parentId: ingredientsId,
      hierarchyName: `Ingredient ${index + 1}`,
      className: detailIngredientTagClass,
      styles: {},
      children: [],
      text: ingredient,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-ingredient-tag'
      }
    });

    ingredientsElement.children.push(ingredientTagId);
  });

  newElements.push(ingredientsElement);

  // Add benefits section
  let currentBenefitId = benefitsId + 300;
  const benefitsElement = {
    id: benefitsId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'div',
    name: 'Product Benefits',
    parentId: infoSectionId,
    hierarchyName: 'Product Benefits',
    className: detailBenefitsClass,
    styles: {},
    children: [],
    text: '',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'herbal-soap-detail-benefits'
    }
  };

  // Add benefits title
  const benefitsTitleId = currentBenefitId++;
  newElements.push({
    id: benefitsTitleId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'h4',
    name: 'Benefits Title',
    parentId: benefitsId,
    hierarchyName: 'Benefits Title',
    styles: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a3d1a',
      marginBottom: '16px',
      marginTop: '0'
    },
    children: [],
    text: 'Key Benefits',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'benefits-title'
    }
  });

  benefitsElement.children.push(benefitsTitleId);

  // Add benefit items
  productData.benefits.forEach((benefit, index) => {
    const benefitItemId = currentBenefitId++;
    const benefitIconId = currentBenefitId++;
    const benefitTextId = currentBenefitId++;

    newElements.push({
      id: benefitItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Benefit Item ${index + 1}`,
      parentId: benefitsId,
      hierarchyName: `Benefit Item ${index + 1}`,
      className: detailBenefitItemClass,
      styles: {},
      children: [benefitIconId, benefitTextId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-detail-benefit-item'
      }
    });

    newElements.push({
      id: benefitIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `Benefit Icon ${index + 1}`,
      parentId: benefitItemId,
      hierarchyName: `Benefit Icon ${index + 1}`,
      styles: {
        color: '#90EE90',
        fontSize: '16px'
      },
      children: [],
      text: '✓',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'benefit-icon'
      }
    });

    newElements.push({
      id: benefitTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: `Benefit Text ${index + 1}`,
      parentId: benefitItemId,
      hierarchyName: `Benefit Text ${index + 1}`,
      styles: {},
      children: [],
      text: benefit,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'benefit-text'
      }
    });

    benefitsElement.children.push(benefitItemId);
  });

  newElements.push(benefitsElement);

  // Add actions section
  const actionsElement = {
    id: actionsId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'div',
    name: 'Product Actions',
    parentId: infoSectionId,
    hierarchyName: 'Product Actions',
    className: detailActionsClass,
    styles: {},
    children: [quantityId, whatsAppId, websiteId],
    text: '',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'herbal-soap-detail-actions'
    }
  };

  // Quantity selector
  const quantityElement = {
    id: quantityId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'div',
    name: 'Quantity Selector',
    parentId: actionsId,
    hierarchyName: 'Quantity Selector',
    className: detailQuantityClass,
    styles: {},
    children: [],
    text: '',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'herbal-soap-detail-quantity'
    },
    innerHTML: `
      <label style="font-size: 14px; font-weight: 600; color: #1a3d1a;">Quantity:</label>
      <select style="padding: 8px 12px; border: 2px solid rgba(144, 238, 144, 0.3); border-radius: 8px; background: white; color: #1a3d1a; font-size: 14px;">
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
      </select>
    `
  };

  // WhatsApp button
  const whatsAppElement = {
    id: whatsAppId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'button',
    name: 'WhatsApp Button',
    parentId: actionsId,
    hierarchyName: 'WhatsApp Button',
    className: detailWhatsAppButtonClass,
    styles: {},
    children: [],
    text: 'Order via WhatsApp',
    visibility: 'visible',
    isExpanded: true,
    attributes: {
      'data-element': 'whatsapp-button',
      'data-whatsapp-phone': '+923086922228',
      'data-whatsapp-message': 'Hi! I want to order the Premium Turmeric Herbal Soap. Can you help me with the ordering process and provide more details about pricing and delivery?'
    },
    interactions: {
      events: [
        {
          id: Date.now() + 2,
          type: 'click',
          action: 'whatsapp-btn-click',
          label: 'Order via WhatsApp'
        }
      ]
    }
  };

  // Website button
  const websiteElement = {
    id: websiteId,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'button',
    name: 'Website Button',
    parentId: actionsId,
    hierarchyName: 'Website Button',
    className: detailWebsiteButtonClass,
    styles: {},
    children: [],
    text: 'Order via Website',
    visibility: 'visible',
    isExpanded: true,
    linkTo: 'order',
    attributes: {
      'data-element': 'herbal-soap-detail-website-button'
    }
  };

  newElements.push(actionsElement, quantityElement, whatsAppElement, websiteElement);

  return {
    elements: newElements
  };
}; 